import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Button,
  Stack,
  useTheme,
  alpha
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTutors,
  setDistance,
  setExam,
  setFilters,
  setGender,
  setIsOnline,
  setLevel,
  setLocation,
  setPrice,
} from "../../../store/search";
import PlaceSelect from "../../../Components/PlaceSelect/PlaceSelect";
import { showErrorMessage } from "../../../utils";
import config from "config";

const FilterBox = ({ setLoading }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { filters } = useSelector((state) => state.search);
  const {
    level,
    gender,
    isOnline,
    price,
    distance,
    postcode,
    exam,
    latitude,
    longitude,
  } = filters;
  const [postcodeError, setPostcodeError] = useState(null);

  const searchHandle = () => {
    if (postcodeError) return showErrorMessage(postcodeError);

    let filterParams = { ...filters }; // Copy all filters

    if (filters.subject) filterParams.subject = filters.subject; // ✅ Ensure subject is included
    if (filters.level) filterParams.level = filters.level;
    if (filters.isOnline > -1) filterParams.isOnline = filters.isOnline;
    if (filters.isOnline === 0) {
      filterParams.distance = filters.distance;
      filterParams.latitude = filters.latitude;
      filterParams.longitude = filters.longitude;
    }
    if (filters.gender > -1) filterParams.gender = filters.gender;
    filterParams.price = filters.price.join(",");
    if (filters.exam) filterParams.exam = filters.exam;

    console.log("Final Filters Sent:", filterParams); // Debugging: Check if subject is included

    dispatch(fetchTutors({ filterParams, setLoading }));
  };

  const clear = () => {
    const defaultFilters = {
      // Keep subject and level unchanged
      subject: filters.subject,
      level: filters.level,
      // Reset only filter box options
      distance: 7,
      gender: -1,
      isOnline: -1,
      price: [15, 100],
      load: 1,
      latitude: -1,
      longitude: -1,
      exam: null,
      postcode: "",
    };
    dispatch(setFilters(defaultFilters));
  };

  //  ------- pricing ---------
  const marks = [
    {
      value: 15,
      label: "\u00A315",
    },
    {
      value: price[0],
      label: "\u00A3" + price[0],
    },
    {
      value: price[1],
      label: "\u00A3" + price[1] + `${price[1] === 100 ? "+" : ""}`,
    },
    {
      value: 100,
      label: "\u00A3100+",
    },
  ];
  if (price[0] < 25) marks.shift();
  if (price[1] > 90) marks.pop();
  const valuetext = (value) =>
    Number(value) > 69 ? `\u00A3${value}+` : `\u00A3${value}`;
  const minDistance = 0;
  const setNewPrice = (_, newPrice, activeThumb) => {
    if (!Array.isArray(newPrice)) {
      return;
    }

    if (activeThumb === 0) {
      dispatch(
        setPrice([Math.min(newPrice[0], price[1] - minDistance), price[1]])
      );
    } else {
      dispatch(
        setPrice([price[0], Math.max(newPrice[1], price[0] + minDistance)])
      );
    }
  };
  const setNewDistance = (_, value) => {
    dispatch(setDistance(value));
  };

  // ---------- location -----------
  const handleIsOnlineChange = (e) => {
    dispatch(setIsOnline(parseInt(e.target.value)));
  };

  const handlePostcodeChange = (e) => {
    dispatch(setLocation(e.target.value));
    // setPostcodeError(e.target.value.error);
  };

  const colourButtonClass =
    config["class-name"] === "math" ? "success" : "secondary";
  const searchColourButtonClass =
    config["class-name"] === "math" ? "primary" : "primary";

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        backgroundColor: '#ffffff',
        height: 'fit-content'
      }}
    >
      <CardContent sx={{ p: { xs: 3, lg: 2.25 } }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700, 
            mb: { xs: 3, lg: 2 },
            fontSize: { lg: '1.5rem' },
            color: theme.palette.text.primary,
            '&::after': {
              content: '""',
              display: 'block',
              width: '40px',
              height: '3px',
              backgroundColor: theme.palette.primary.main,
              borderRadius: '2px',
              marginTop: '8px'
            }
          }}
        >
          Filter options
        </Typography>
        <Box
          component="form"
          onSubmit={null}
          sx={{
            "& .MuiTextField-root": { m: 1, maxWidth: "100%" },
            "& .captcha": { m: 1, mt: 2 },
            "& .heading": { m: 1, textAlign: "center" },
          }}
          noValidate
          autoComplete="off"
        >
          <Stack spacing={{ xs: 3, lg: 2 }}>
            {/* Location Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1.5, lg: 1 }, color: theme.palette.text.primary }}>
                Location
              </Typography>
              <FormControl component="fieldset" variant="standard" fullWidth>
                <RadioGroup
                  aria-labelledby="location"
                  value={isOnline}
                  onChange={handleIsOnlineChange}
                  defaultValue={1}
                  row
                  sx={{ gap: { xs: 2, lg: 1.5 } }}
                >
                  <FormControlLabel
                    control={<Radio value={1} sx={{ color: theme.palette.primary.main }} />}
                    label="Online"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontWeight: 500,
                        color: theme.palette.text.primary 
                      }
                    }}
                  />
                  <FormControlLabel
                    control={<Radio value={0} sx={{ color: theme.palette.primary.main }} />}
                    label="In-person"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontWeight: 500,
                        color: theme.palette.text.primary 
                      }
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            {/* Postcode and Distance (only for In-person) */}
            {isOnline === 0 && (
              <>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1.5, lg: 1 }, color: theme.palette.text.primary }}>
                    Postcode (UK only)
                  </Typography>
                  <PlaceSelect
                    style={{ width: "100%", margin: 0 }}
                    value={postcode}
                    onChange={handlePostcodeChange}
                    error={postcodeError}
                  />
                  {!!postcodeError && (
                    <FormHelperText error={true} sx={{ mt: 1 }}>
                      {postcodeError}
                    </FormHelperText>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1.5, lg: 1 }, color: theme.palette.text.primary }}>
                    Distance: Within {distance} Miles
                  </Typography>
                  <Box sx={{ px: { xs: 2, lg: 1 } }}>
                    <Slider
                      disabled={!postcode}
                      aria-label="distance"
                      valueLabelDisplay="auto"
                      step={1}
                      marks
                      min={1}
                      max={30}
                      value={distance}
                      onChange={setNewDistance}
                      sx={{
                        color: theme.palette.primary.main,
                        '& .MuiSlider-thumb': {
                          backgroundColor: theme.palette.primary.main,
                        },
                        '& .MuiSlider-track': {
                          backgroundColor: theme.palette.primary.main,
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: theme.palette.grey[300],
                        }
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}
            {/* Pricing Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1.5, lg: 1 }, color: theme.palette.text.primary }}>
                Pricing: {valuetext(price[0])} - {valuetext(price[1])}
              </Typography>
              <Box sx={{ px: { xs: 2, lg: 1 } }}>
                <Slider
                  getAriaLabel={() => "Price range"}
                  value={price}
                  onChange={setNewPrice}
                  valueLabelDisplay="auto"
                  getAriaValueText={valuetext}
                  marks={marks}
                  min={15}
                  max={100}
                  disableSwap
                  sx={{
                    color: theme.palette.primary.main,
                    '& .MuiSlider-thumb': {
                      backgroundColor: theme.palette.primary.main,
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: theme.palette.primary.main,
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: theme.palette.grey[300],
                    }
                  }}
                />
              </Box>
            </Box>
            {/* Exam Board Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1.5, lg: 1 }, color: theme.palette.text.primary }}>
                Exam board
              </Typography>
              <FormControl fullWidth>
                <Select
                  id="exam-board"
                  value={exam || " "}
                  placeholder="Exam Board"
                  onChange={(e) =>
                    dispatch(
                      setExam(
                        e.target.value === " " ? null : e.target.value
                      )
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >
                  <MenuItem value={" "}>
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="AQA">AQA</MenuItem>
                  <MenuItem value="OCR">OCR</MenuItem>
                  <MenuItem value="Edexcel">Edexcel</MenuItem>
                  <MenuItem value="WJEC">WJEC</MenuItem>
                  <MenuItem value="CCEA">CCEA</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Gender Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: { xs: 1.5, lg: 1 }, color: theme.palette.text.primary }}>
                Gender
              </Typography>
              <FormControl fullWidth>
                <Select
                  id="gender-select"
                  value={gender}
                  onChange={(e) =>
                    dispatch(setGender(parseInt(e.target.value)))
                  }
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >
                  <MenuItem value={-1}>
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value={0}>Male</MenuItem>
                  <MenuItem value={1}>Female</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>

          {/* Action Buttons */}
          <Box sx={{ mt: { xs: 3, lg: 2 }, pt: { xs: 2, lg: 1.5 }, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" spacing={{ xs: 2, lg: 1.5 }}>
              <Button
                type="button"
                variant="outlined"
                size="large"
                fullWidth
                onClick={clear}
                sx={{
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  py: { lg: 1 },
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                Clear Filters
              </Button>
              <Button
                type="button"
                variant="contained"
                size="large"
                fullWidth
                onClick={searchHandle}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  fontWeight: 600,
                  py: { lg: 1 },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  }
                }}
              >
                Search
              </Button>
            </Stack>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FilterBox;
